"use client";

import { errorHandler } from "@/info/placeholders";
import { DEFAULT_IMAGE_QUALITY_BLUR, DEFAULT_IMAGE_QUALITY_MAIN, normalizeQuality, type ImageQuality } from "@/lib/constants/images";
import { cn } from "@/utils/cn";
import useEmblaCarousel from "embla-carousel-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import * as React from "react";
import "./fullscreen-transitions.css";
import { PinchZoomImage } from "./pinch-zoom-image";
const GalleryVideoSlidePlyr = dynamic(() => import("./gallery-video-slide-plyr").then(mod => mod.GalleryVideoSlidePlyr), {
  ssr: false
});
export interface GalleryMediaItem {
  url: string;
  alt?: string;
  space?: string;
  name?: string;
  priority?: boolean;
  isVideo?: boolean;
  videoCaptionsSrc?: string;
  showCaptionsByDefault?: boolean;
  /** Cover/poster image URL for video items, used as blurred background */
  posterUrl?: string;
}
export interface GalleryMediaCategory {
  name: string;
  code: string;
}
export interface MediaGalleryProps {
  media: GalleryMediaItem[];
  categories?: GalleryMediaCategory[];
  className?: string;
  showNavigationButtons?: boolean;
  showMediaCounter?: boolean;
  enableKeyboardNavigation?: boolean;
  enableDoubleTapFullscreen?: boolean;
  autoLoop?: boolean;
  children?: React.ReactNode; // For overlays like dots, categories, etc.
  // Image optimization props
  sizes?: string;
  quality?: ImageQuality | number;
  onMainImageClick?: () => void;
  videoHideControls?: boolean;
  /** Vertical alignment of images - 'center' (default) or 'top' */
  imageAlignment?: "center" | "top";
  /** Use darker background for better contrast (e.g., for card galleries with text overlays) */
  darkerBackground?: boolean;
  /** Autoplay video on load (for preference list) */
  videoAutoplayOnLoad?: boolean;
}
interface MediaGalleryContextType {
  currentIndex: number;
  media: GalleryMediaItem[];
  goToIndex: (index: number) => void;
  goToPrevious: () => void;
  goToNext: () => void;
  toggleFullscreen: () => void;
  isFullscreen: boolean;
  emblaApi: ReturnType<typeof useEmblaCarousel>[1];
}
const MediaGalleryContext = React.createContext<MediaGalleryContextType | null>(null);
export function MediaGallery({
  media,
  categories: _categories = [],
  className,
  showNavigationButtons = true,
  showMediaCounter = true,
  enableKeyboardNavigation = true,
  enableDoubleTapFullscreen = true,
  autoLoop = true,
  children,
  // Image optimization props
  sizes,
  quality,
  onMainImageClick,
  videoHideControls = false,
  imageAlignment = "center",
  darkerBackground = false,
  videoAutoplayOnLoad = false
}: MediaGalleryProps) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  // Use ref instead of state for hover to prevent re-renders, as it's only used for logic guards
  const isHoveredRef = React.useRef(false);

  // Hover handlers with useCallback to prevent re-renders
  const handleMouseEnter = React.useCallback(() => {
    isHoveredRef.current = true;
  }, []);
  const handleMouseLeave = React.useCallback(() => {
    isHoveredRef.current = false;
  }, []);

  // Track initial touch position to determine drag direction
  const touchStartRef = React.useRef<{
    x: number;
    y: number;
  } | null>(null);

  // Embla carousel with axis lock to allow vertical scrolling to pass through
  const [emblaRef, emblaApi_0] = useEmblaCarousel({
    loop: autoLoop,
    axis: "x",
    // Lock to horizontal axis only
    dragFree: false,
    // Increase threshold to prevent accidental horizontal drags during vertical scrolling
    dragThreshold: 10,
    // Allow vertical scrolling to pass through to parent (for closing overlay)
    watchDrag: (emblaApi, event) => {
      const target = event.target as HTMLElement;

      // Don't interfere with pinch gestures (multi-touch) - important for zoom
      const touchEvent = event as TouchEvent;
      if (touchEvent.touches && touchEvent.touches.length > 1) {
        touchStartRef.current = null;
        return false;
      }

      // Track touch start position
      if (touchEvent.type === "touchstart" && touchEvent.touches && touchEvent.touches[0]) {
        touchStartRef.current = {
          x: touchEvent.touches[0].clientX,
          y: touchEvent.touches[0].clientY
        };
      }

      // Detect drag direction on touchmove
      if (touchEvent.type === "touchmove" && touchEvent.touches && touchEvent.touches[0] && touchStartRef.current) {
        const deltaX = Math.abs(touchEvent.touches[0].clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touchEvent.touches[0].clientY - touchStartRef.current.y);

        // If vertical movement is greater than horizontal, don't let Embla handle it
        if (deltaY > deltaX && deltaY > 5) {
          touchStartRef.current = null;
          return false;
        }
      }

      // Allow drag on images and carousel container
      return !target.closest("button") && !target.closest('a:not([role="button"])');
    }
  });
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Double tap state for fullscreen
  const lastTapRef = React.useRef<number>(0);
  const tapCountRef = React.useRef<number>(0);
  const clickTimeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);

  // Navigation functions
  const goToPrevious = React.useCallback(() => {
    emblaApi_0?.scrollPrev();
  }, [emblaApi_0]);
  const goToNext = React.useCallback(() => {
    emblaApi_0?.scrollNext();
  }, [emblaApi_0]);
  const goToIndex = React.useCallback((index: number) => {
    if (index >= 0 && index < media.length) {
      emblaApi_0?.scrollTo(index);
    }
  }, [media.length, emblaApi_0]);

  // Check if fullscreen API is supported
  const isFullscreenSupported = React.useMemo(() => {
    if (typeof document === "undefined") return false;
    return !!(document.fullscreenEnabled ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (document as any).webkitFullscreenEnabled ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (document as any).mozFullScreenEnabled ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (document as any).msFullscreenEnabled);
  }, []);

  // Fullscreen functionality with smooth transitions
  const toggleFullscreen = React.useCallback(() => {
    if (!containerRef.current || isTransitioning) return;

    // If fullscreen API is not supported (iOS Safari, some mobile browsers),
    // toggle a simulated fullscreen using CSS/state
    if (!isFullscreenSupported) {
      setIsTransitioning(true);
      setIsFullscreen(prev => !prev);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
      return;
    }
    setIsTransitioning(true);
    const container = containerRef.current;

    // Add pre-transition class for smooth scaling
    container.classList.add("transitioning-fullscreen");

    // Helper to finish transition
    const finishTransition = () => {
      setTimeout(() => {
        setIsTransitioning(false);
        container.classList.remove("transitioning-fullscreen");
      }, 300); // Match CSS transition duration
    };

    // Helper to abort transition
    const abortTransition = () => {
      setIsTransitioning(false);
      container.classList.remove("transitioning-fullscreen");
    };

    // @ts-ignore
    const isFullscreenActive = document.fullscreenElement ||
    // @ts-ignore
    document.webkitFullscreenElement ||
    // @ts-ignore
    document.mozFullScreenElement ||
    // @ts-ignore
    document.msFullscreenElement;
    if (isFullscreenActive) {
      // Exit fullscreen with smooth transition
      const exitFunc = document.exitFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).webkitExitFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).mozCancelFullScreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).msExitFullscreen;
      if (typeof exitFunc === "function") {
        const p = exitFunc.call(document);
        if (p && typeof p.then === "function") {
          p.then(finishTransition).catch(abortTransition);
        } else {
          finishTransition();
        }
      } else {
        abortTransition();
      }
    } else {
      // Enter fullscreen with smooth transition
      const requestFunc = container.requestFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).webkitRequestFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).mozRequestFullScreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).msRequestFullscreen ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (container as any).webkitEnterFullscreen; // Video specific, but for completeness

      if (typeof requestFunc === "function") {
        try {
          const p_0 = requestFunc.call(container);
          if (p_0 && typeof p_0.then === "function") {
            p_0.then(finishTransition).catch(abortTransition);
          } else {
            finishTransition();
          }
        } catch (_e) {
          abortTransition();
        }
      } else {
        abortTransition();
      }
    }
  }, [isTransitioning, isFullscreenSupported]);

  // Double tap handler for fullscreen - FULLY RESTORED
  const handleDoubleTap = React.useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // Always prevent default behavior and bubbling to ensure we control the click
    e.preventDefault();
    e.stopPropagation();
    if ("nativeEvent" in e) {
      e.nativeEvent.stopImmediatePropagation();
    }

    // Clear any pending single click
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = undefined;
    }
    if (!enableDoubleTapFullscreen) {
      // If double tap disabled, just treat as single click immediately
      onMainImageClick?.();
      return;
    }
    const now = Date.now();
    const timeDiff = now - lastTapRef.current;
    if (timeDiff < 300 && timeDiff > 0) {
      // Double tap detected
      toggleFullscreen();
      tapCountRef.current = 0;
    } else {
      // Potential single tap
      tapCountRef.current = 1;
      // Delay single click action to check for double tap
      clickTimeoutRef.current = setTimeout(() => {
        onMainImageClick?.();
      }, 300);
    }
    lastTapRef.current = now;
  }, [enableDoubleTapFullscreen, toggleFullscreen, onMainImageClick]);

  // Keyboard navigation
  const handleKeyDown = React.useCallback((event_0: KeyboardEvent) => {
    if (!enableKeyboardNavigation) return;

    // Only handle keyboard navigation if gallery is hovered or focused, or in fullscreen
    if (!isHoveredRef.current && !isFullscreen) return;
    switch (event_0.key) {
      case "ArrowLeft":
        event_0.preventDefault();
        event_0.stopPropagation();
        goToPrevious();
        break;
      case "ArrowRight":
        event_0.preventDefault();
        event_0.stopPropagation();
        goToNext();
        break;
    }
  }, [enableKeyboardNavigation, goToPrevious, goToNext, isFullscreen]);

  // Embla carousel event handlers
  React.useEffect(() => {
    if (!emblaApi_0) return;
    const onSelect = () => {
      setCurrentIndex(emblaApi_0.selectedScrollSnap());
    };
    emblaApi_0.on("select", onSelect);
    onSelect(); // Set initial index

    return () => {
      emblaApi_0.off("select", onSelect);
    };
  }, [emblaApi_0]);

  // Handle fullscreen change event with smooth transitions
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;

      // Add a small delay to allow browser to complete fullscreen transition
      requestAnimationFrame(() => {
        setIsFullscreen(isNowFullscreen);
      });
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Keyboard event listener
  React.useEffect(() => {
    // Use capture phase to handle the event before it can bubble
    document.addEventListener("keydown", handleKeyDown, {
      capture: true
    });
    return () => document.removeEventListener("keydown", handleKeyDown, {
      capture: true
    });
  }, [handleKeyDown]);

  // Current media item
  const currentMedia = media[currentIndex];

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => ({
    currentIndex,
    media,
    goToIndex,
    goToPrevious,
    goToNext,
    toggleFullscreen,
    isFullscreen,
    emblaApi: emblaApi_0
  }), [currentIndex, media, goToIndex, goToPrevious, goToNext, toggleFullscreen, isFullscreen, emblaApi_0]);
  if (!media || media.length === 0) {
    return <div className="flex items-center justify-center w-full h-96 bg-gray-100">
				<div className="text-center">
					<div className="w-16 h-16 bg-gray-300 mx-auto mb-4" />
					<p className="text-gray-500">No media available</p>
				</div>
			</div>;
  }
  return <div ref={containerRef} className={cn("group relative w-full h-full bg-white overflow-hidden focus:outline-none",
  // Simulated fullscreen for browsers that don't support fullscreen API (iOS Safari)
  isFullscreen && !isFullscreenSupported && "fixed inset-0 z-[9999] w-screen h-screen", className)} tabIndex={0} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out",
    willChange: "transform, opacity"
  }}>
			{/* Main Image */}
			<div className="relative w-full h-full overflow-hidden">
				{/* Background blur effect - decorative blurred image behind the current slide */}
				{(() => {
        const blurSrc = currentMedia?.isVideo ? currentMedia?.posterUrl : currentMedia?.url;
        return blurSrc ? <>
							{/* Top half: Standard blur background */}
							<div className="absolute inset-[-2rem] overflow-hidden transition-all duration-300 ease-out" style={{
            opacity: isFullscreen ? 0.1 : darkerBackground ? 0.6 : 0.3,
            transform: isTransitioning ? "scale(1.05)" : "scale(1)"
          }}>
								<Image src={blurSrc} alt="" fill className="object-cover transition-all duration-300 ease-out" style={{
              filter: isFullscreen ? "blur(2rem)" : "blur(1rem)"
            }}
            // Prioritize first media background blur for LCP optimization
            priority={currentMedia?.priority} fetchPriority={currentMedia?.priority ? "high" : "low"} loading={currentMedia?.priority ? "eager" : "lazy"} sizes={sizes || "100vw"} quality={normalizeQuality(quality, DEFAULT_IMAGE_QUALITY_BLUR)} onError={errorHandler} unoptimized={blurSrc?.endsWith(".svg")} />
								{/* Dark gradient overlay for better text contrast (only for card galleries) */}
								{darkerBackground && !currentMedia?.isVideo && <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />}
							</div>

							{/* Bottom reflection: Only for card galleries with top-aligned images */}
							{darkerBackground && imageAlignment === "top" && !currentMedia?.isVideo && <div className="absolute bottom-0 left-0 right-0 h-[40%] overflow-hidden" style={{
            opacity: 0.3
          }}>
									{/* <Image
          src={blurSrc}
          alt=""
          fill
          className="object-cover transition-all duration-300 ease-out"
          style={{
          	filter: "blur(1rem)",
          	transform: "scaleY(-1)", // Flip vertically for reflection
          	objectPosition: "top", // Align to top (which is bottom when flipped)
          }}
          priority={currentMedia?.priority}
          fetchPriority="low"
          loading="lazy"
          sizes={sizes || "100vw"}
          quality={normalizeQuality(quality, DEFAULT_IMAGE_QUALITY_BLUR)}
          onError={errorHandler}
          unoptimized={blurSrc?.endsWith(".svg")}
          /> */}
									{/* Gradient fade for reflection effect */}
									<div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60" />
								</div>}
						</> : null;
      })()}

				{/* Embla Carousel */}
				<div ref={emblaRef} className="h-full" style={{
        // In fullscreen, allow pinch-zoom. Outside fullscreen, allow vertical panning for overlay close
        // pan-y tells browser to handle vertical scrolling (important for overlay close)
        // manipulation removes default double-tap zoom delay
        touchAction: isFullscreen ? "manipulation pinch-zoom" : "pan-y manipulation"
      }}>
					<div className="flex h-full">
						{media.map((item, index_0) => {
            // Load eagerly: first item (LCP), current item, and adjacent items
            const isFirstItem = index_0 === 0;
            const isNearCurrentItem = Math.abs(index_0 - currentIndex) <= 1;
            const shouldLoadEagerly = isFirstItem || isNearCurrentItem;
            const isPriorityItem = item.priority === true || isFirstItem;
            const isFloorPlan = item.space === "floorplan";
            const shouldEnableZoom = isFullscreen && isFloorPlan;

            // Performance Optimization: Lazy mounting
            // Only mount content for current slide and adjacent slides (buffer)
            // This significantly reduces DOM nodes and memory usage for large galleries
            // especially when multiple galleries are rendered on a page (e.g. lists)
            let shouldRenderContent = isNearCurrentItem;
            if (autoLoop && media.length > 1) {
              const lastIndex = media.length - 1;
              // Handle wrap-around for loop mode
              if (currentIndex === 0 && index_0 === lastIndex) shouldRenderContent = true;
              if (currentIndex === lastIndex && index_0 === 0) shouldRenderContent = true;
            }
            return <div key={index_0} className={cn("flex-[0_0_100%] min-w-0 relative cursor-pointer flex justify-center", imageAlignment === "top" ? "items-start md:items-center" : "items-center",
            // Boost z-index for video slides when they're current (for controls interaction)
            item?.isVideo && index_0 === currentIndex && "z-20")} onClick={shouldEnableZoom || item?.isVideo ? undefined : handleDoubleTap} style={{
              transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out",
              transform: isTransitioning ? "scale(0.95)" : "scale(1)",
              opacity: isTransitioning && isFullscreenSupported ? 0.95 : 1
            }}>
									{shouldRenderContent ? item?.isVideo ? <GalleryVideoSlidePlyr src={item.url} isCurrentSlide={index_0 === currentIndex} isFullscreen={isFullscreen} captionsSrc={item.videoCaptionsSrc} showCaptionsByDefault={item.showCaptionsByDefault} hideControls={videoHideControls} autoplayOnLoad={videoAutoplayOnLoad} /> : item?.url ? shouldEnableZoom ? <PinchZoomImage src={item.url} alt={item.alt || `Slide ${index_0 + 1}`} sizes={sizes || "100vw"} quality={normalizeQuality(quality, DEFAULT_IMAGE_QUALITY_MAIN)} priority={isPriorityItem} fetchPriority={isPriorityItem ? "high" : "low"} loading={shouldLoadEagerly ? "eager" : "lazy"} onError={errorHandler} /> : <Image src={item.url} alt={item.alt || `Slide ${index_0 + 1}`} fill className={cn("transition-all duration-300 ease-out", imageAlignment === "top" ? "object-contain object-top md:object-center" : "object-contain")} sizes={sizes || "100vw"} quality={normalizeQuality(quality, DEFAULT_IMAGE_QUALITY_MAIN)} priority={isPriorityItem} fetchPriority={isPriorityItem ? "high" : "low"} loading={shouldLoadEagerly ? "eager" : "lazy"} onError={errorHandler} unoptimized={item.url?.endsWith(".svg")} /> : <div className="w-full h-full bg-gray-200 flex items-center justify-center">
												<span className="text-gray-500 text-sm">No media available</span>
											</div> : null}
								</div>;
          })}
					</div>
				</div>
			</div>

			{/* Media Counter */}
			{showMediaCounter && media.length > 1 && <div className="absolute top-3 left-3 z-10 flex items-center h-6">
					<div className="bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
						{currentIndex + 1} / {media.length}
						{currentMedia.name && ` • ${currentMedia.name}`}
					</div>
				</div>}

			{/* Custom overlays (dots, categories, etc.) */}
			<MediaGalleryContext.Provider value={contextValue}>{children}</MediaGalleryContext.Provider>
		</div>;
}

// Hook to provide gallery control functions to children
export function useMediaGallery() {
  const context = React.useContext(MediaGalleryContext);
  if (!context) {
    throw new Error("useMediaGallery must be used within a MediaGallery component");
  }
  return context;
}